import axios, { AxiosError } from "axios";
import { AjaxError } from "rxjs/ajax";
import HeapsError from "./heaps-error";
import { useEffect } from "react";
import { toastr } from "react-redux-toastr";

type Error = {
  code: string; // TODO: make use of error codes in the future
  detail: string;
};

// this is format that comes from our backend
type ErrorResponse =
  | {
      errors: Error[];
    }
  // For GET requests
  | { error: number; message: string };

const generalFallback = "Something went wrong";

type Args = {
  error: AjaxError;
  fallback?: string;
};

const extractError = (errorResponse: ErrorResponse, fallback?: string) => {
  if ("errors" in errorResponse && errorResponse.errors?.length > 0) {
    return errorResponse.errors[0].detail;
  }
  return fallback || generalFallback;
};

export const extractAjaxError = ({ error, fallback }: Args): string => {
  const errorResponse: ErrorResponse = error.xhr.response;
  return extractError(errorResponse, fallback);
};

/* ----- Axios error handling below ----- */

export const extractErrorMessageAndErrorCode = (error: unknown) => {
  const fallbackError = {
    errorMessage: generalFallback,
    errorCode: "unknown_error",
  };

  if (!axios.isAxiosError(error)) {
    // unexpected error occurred
    return fallbackError;
  }
  const typedError = error as AxiosError;
  const errorResponse = typedError.response!.data as ErrorResponse;

  if ("errors" in errorResponse && errorResponse.errors?.length > 0) {
    const errorCode = errorResponse.errors[0].code;
    const errorDetail =
      errorResponse.errors[0].detail ||
      errorResponse.errors[0].code ||
      fallbackError.errorMessage;

    return { errorMessage: errorDetail, errorCode };
  }

  if ("error" in errorResponse && "message" in errorResponse) {
    const errorDetail = errorResponse.message;
    return { errorMessage: errorDetail, errorCode: "unknown_error" };
  }

  return fallbackError;
};

export const getErrorMessage = (error: unknown) => {
  if (error instanceof HeapsError) {
    return error.errorMessage;
  }

  return generalFallback;
};

export const useHandleFetchError = (error: unknown) => {
  return useEffect(() => {
    if (!error) {
      return;
    }
    const errorMessage = getErrorMessage(error);
    toastr.error("Error", errorMessage);
  }, [error]);
};
